import { Policy } from '../../js/customer/Policy';
import { Application } from '../../js/customer/Application';
import { LaunchDarklyInstance, LD_APP } from '../../js/launch-darkly/LaunchDarklySingleton';
import { AdminUtil } from '../admin/AdminUtil';
import { amorphicStatic } from '@haventech/amorphic';
import { serverOnly } from '../helpers';
import { ExamVendor } from '../types/CommonTypes';
import { CasApplication } from '../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';
import { isInParamed } from '../../../coverpath-agent/public/js/utility/AgentUtilities';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

export { ExamVendor } from '../types/CommonTypes';

export enum ExamOrderResponse {
  SUCCESS = 'Success',
  PENDING = 'Pending',
  APPS_CAN_SCHEDULE = 'APPSCanSchedule',
  APPS_CANNOT_SCHEDULE = 'APPSCannotSchedule',
}

export class ExamSchedulingStatus {
  public static async canSchedule(policy: Policy, application: CasApplication): Promise<boolean> {
    if (!isInParamed(policy.workflowState, policy.workflowSubState)) {
      return false;
    }

    //check LD flag first:
    const examSchedulingFeatureFlag = await ExamSchedulingStatus.getFeatureFlagValue(policy, application);
    const examOptOutFeatureFlag = await ExamSchedulingStatus.getExamOptOutFeatureFlagValue(policy);

    if (examSchedulingFeatureFlag) {
      //if there is already a scheduled date, don't allow scheduling
      if (
        ExamSchedulingStatus.isExamScheduled(policy) ||
        ExamSchedulingStatus.isHaveVendorCallClientElected(application)
      ) {
        LoggerSingleton.getInstance().info(
          {
            module: 'ExamSchedulingStatus',
            function: 'canSchedule',
            category: Category.request,
          },
          'Status for CanSchedule is false because there is already a date provided or haveExamVendorCallClient was elected'
        );
        return false;
      }

      if ((await ExamSchedulingStatus.getUwWorkflowStage(policy.policyNumber)) !== 'SL1 Schedule Labs') {
        LoggerSingleton.getInstance().info(
          {
            module: 'ExamSchedulingStatus',
            function: 'canSchedule',
            category: Category.request,
          },
          'Status for CanSchedule is false because the UW workflow stage is not SL1'
        );
        return false;
      }

      if (application.getExamVendor() === ExamVendor.APPS) {
        //when vendor is APPS check if APPS Can Schedule:
        return application.examOrderResponse === ExamOrderResponse.APPS_CAN_SCHEDULE;
      } else {
        //when vendor is ExamOne check if the paramed order response was successful:
        return application.examOrderResponse === ExamOrderResponse.SUCCESS;
      }
    }

    if (examOptOutFeatureFlag && application.examOptOut) {
      return false;
    }

    //feature flag is off
    return false;
  }

  /**
   * if there already exits a value in onlineParamedScheduleDate or paramedScheduledDate the exam is already scheduled
   * @param policy
   */
  public static isExamScheduled(policy: Policy): boolean {
    return !!(policy.onlineParamedScheduledDate || policy.paramedScheduledDate);
  }

  /**
   * if the field haveExamVendorCallClient is true, advisor has elected not to schedule
   * @param application
   */
  public static isHaveVendorCallClientElected(application: Application): boolean {
    return !!application.haveExamVendorCallClient;
  }

  @serverOnly()
  public static async getFeatureFlagValue(policy: Policy, application: Application): Promise<boolean> {
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);

    const examVendor: string = application.getExamVendor();

    const flagResult = await ldInstance.getFeatureFlagValueAsBooleanWithIDAndCustom(
      'examScheduleEnabled',
      policy.primaryPolicyAgent.primaryAgent.producerId,
      {
        vendor: examVendor,
        state: policy.insured.person.residentialAddress.state,
        agencyId: policy.primaryPolicyAgent.primaryAgent.agencyId,
      }
    );

    return flagResult;
  }

  @serverOnly()
  public static async getExamOptOutFeatureFlagValue(policy: Policy): Promise<boolean> {
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);

    const flagResult = await ldInstance.getFeatureFlagValueAsBooleanWithId(
      'examOptOutEnabled',
      policy.primaryPolicyAgent.primaryAgent.producerId
    );

    return flagResult;
  }

  public static async getUwWorkflowStage(policy: string): Promise<string> {
    const uwWorkflow = await AdminUtil.getCurrentUWWorkflowStage(policy);
    return uwWorkflow.currentStage;
  }
}
